import { TemplateDto } from 'app/models/generated'
import { logger } from 'app/logging/Logger'
import {
    executeQuery,
    executeMutation,
    fetchVersion,
} from 'app/api/client/graphqlClient'
import {
    consultTemplateDtosByConsultId,
    createConsultTemplateDtoMutation,
    updateConsultTemplateDtoMutation,
    deleteConsultTemplateDtoMutation,
} from 'app/graphql/customQueries'
import { isNotDeleted } from './softDelete'

/**
 * Repository interface for managing template data.
 * Provides CRUD operations for ConsultTemplateDto records.
 */
export interface ConsultTemplateRepository {
    /**
     * Get template for a consult
     * @param consultId The consult ID
     * @returns The template or null if not found
     */
    getByConsultId(consultId: string): Promise<TemplateDto | null>

    /**
     * Get the full ConsultTemplateDto record (including its record `id`) for a
     * consult. Unlike {@link getByConsultId} — which returns only the embedded
     * `TemplateDto` blob — this exposes the persisted record id so callers
     * (e.g. the admin template-management update form) can bind to and update
     * the correct record.
     * @param consultId The consult ID
     * @returns The record or null if not found
     */
    getRecordByConsultId(
        consultId: string
    ): Promise<ConsultTemplateRecord | null>

    /**
     * Create or update template for a consult
     * @param consultId The consult ID
     * @param template The template to save
     */
    saveTemplate(consultId: string, template: TemplateDto): Promise<void>

    /**
     * Delete template for a consult
     * @param consultId The consult ID
     */
    deleteTemplate(consultId: string): Promise<void>
}

/**
 * A persisted ConsultTemplateDto record with its record `id` exposed.
 * Used where the record identity (not just the embedded template) is needed,
 * such as binding the generated `ConsultTemplateDtoUpdateForm`.
 */
export interface ConsultTemplateRecord {
    id: string
    consultId: string
    owner?: string
    template: TemplateDto | null
}

interface ConsultTemplateDtoResult {
    id: string
    consultId: string
    owner?: string
    template?: TemplateDto | null
    templateDelta?: (TemplateDto | null)[] | null
}

interface ConsultTemplateDtosByConsultIdResult {
    items: ConsultTemplateDtoResult[]
    nextToken: string | null
}

/**
 * GraphQL API implementation of ConsultTemplateRepository.
 * Manages template data via AppSync GraphQL operations.
 */
export class DataStoreConsultTemplateRepository
    implements ConsultTemplateRepository
{
    async getByConsultId(consultId: string): Promise<TemplateDto | null> {
        try {
            logger.debug('Querying template by consultId', {
                action: 'get_template',
                metadata: { consultId },
            })

            const result =
                await executeQuery<ConsultTemplateDtosByConsultIdResult>(
                    consultTemplateDtosByConsultId,
                    { consultId },
                    'consultTemplateDtosByConsultId'
                )

            const items = result.items.filter(isNotDeleted)

            if (items.length === 0) {
                logger.debug('No template found for consult', {
                    action: 'get_template_not_found',
                    metadata: { consultId },
                })
                return null
            }

            if (items.length > 1) {
                logger.warn('Multiple template records found for consult', {
                    action: 'get_template_multiple',
                    metadata: { consultId, count: items.length },
                })
            }

            const record = items[0]
            logger.debug('Template found', {
                action: 'get_template_success',
                metadata: { consultId, recordId: record.id },
            })
            return record.template || null
        } catch (error) {
            logger.error('Failed to fetch template', error as Error, {
                action: 'get_template_by_consult_id',
                metadata: { consultId },
            })
            throw error
        }
    }

    async getRecordByConsultId(
        consultId: string
    ): Promise<ConsultTemplateRecord | null> {
        try {
            const result =
                await executeQuery<ConsultTemplateDtosByConsultIdResult>(
                    consultTemplateDtosByConsultId,
                    { consultId },
                    'consultTemplateDtosByConsultId'
                )

            const items = result.items.filter(isNotDeleted)

            if (items.length === 0) {
                return null
            }

            const record = items[0]
            return {
                id: record.id,
                consultId: record.consultId,
                owner: record.owner,
                template: record.template ?? null,
            }
        } catch (error) {
            logger.error('Failed to fetch template record', error as Error, {
                action: 'get_template_record_by_consult_id',
                metadata: { consultId },
            })
            throw error
        }
    }

    async saveTemplate(
        consultId: string,
        template: TemplateDto
    ): Promise<void> {
        try {
            // Query for existing template record by consultId
            const result =
                await executeQuery<ConsultTemplateDtosByConsultIdResult>(
                    consultTemplateDtosByConsultId,
                    { consultId },
                    'consultTemplateDtosByConsultId'
                )

            const existing = result.items.filter(isNotDeleted)

            if (existing.length > 0) {
                // Update existing record
                const record = existing[0]
                const _version = await fetchVersion(
                    'ConsultTemplateDto',
                    record.id
                )

                await executeMutation(
                    updateConsultTemplateDtoMutation,
                    {
                        input: {
                            id: record.id,
                            template,
                            _version,
                        },
                    },
                    'updateConsultTemplateDto'
                )

                logger.info('Template updated successfully', {
                    action: 'update_template',
                    metadata: { consultId, templateId: template.id },
                })
            } else {
                // Create new record
                await executeMutation(
                    createConsultTemplateDtoMutation,
                    {
                        input: {
                            consultId,
                            template,
                        },
                    },
                    'createConsultTemplateDto'
                )

                logger.info('Template created successfully', {
                    action: 'create_template',
                    metadata: { consultId, templateId: template.id },
                })
            }
        } catch (error) {
            logger.error('Failed to save template', error as Error, {
                action: 'save_template',
                metadata: { consultId },
            })
            throw error
        }
    }

    async deleteTemplate(consultId: string): Promise<void> {
        try {
            // Query for existing template record by consultId
            const result =
                await executeQuery<ConsultTemplateDtosByConsultIdResult>(
                    consultTemplateDtosByConsultId,
                    { consultId },
                    'consultTemplateDtosByConsultId'
                )

            const existing = result.items.filter(isNotDeleted)

            if (existing.length > 0) {
                const record = existing[0]
                const _version = await fetchVersion(
                    'ConsultTemplateDto',
                    record.id
                )

                await executeMutation(
                    deleteConsultTemplateDtoMutation,
                    {
                        input: {
                            id: record.id,
                            _version,
                        },
                    },
                    'deleteConsultTemplateDto'
                )

                logger.info('Template deleted successfully', {
                    action: 'delete_template',
                    metadata: { consultId },
                })
            } else {
                logger.debug('No template to delete', {
                    action: 'delete_template_not_found',
                    metadata: { consultId },
                })
            }
        } catch (error) {
            logger.error('Failed to delete template', error as Error, {
                action: 'delete_template',
                metadata: { consultId },
            })
            throw error
        }
    }
}

// Singleton instance
export const consultTemplateRepository =
    new DataStoreConsultTemplateRepository()
